import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../components/ui/use-toast';
import { useT } from '../../context/LanguageContext';
import loyaltyService from '../../services/loyaltyService';
import { Settings, Plus, Pencil, Trash2 } from 'lucide-react';

/*
 * Programme Settings — every number of the scheme, plus the tier ladder and
 * the bonus earn rules. Nothing on this page is compiled into the product:
 * changing the programme IS this page, never a deploy.
 *
 * The three redemption modes change which panels matter, so the form shows
 * only the fields the chosen mode reads — a rate field left visible under a
 * scheme that stopped honouring it is an invitation to believe it.
 */

const Row = ({ label, hint, children }) => (
  <div className="grid md:grid-cols-[240px,1fr] items-start gap-2 py-2">
    <div>
      <div className="text-sm font-medium text-gray-800">{label}</div>
      {hint && <div className="text-[11px] text-gray-500">{hint}</div>}
    </div>
    <div className="max-w-sm">{children}</div>
  </div>
);

const Toggle = ({ checked, onChange }) => (
  <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? 'bg-emerald-500' : 'bg-gray-300'}`}>
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
      checked ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

export default function LoyaltyProgramSettings() {
  const { t } = useT();
  const { toast } = useToast();
  const [p, setP] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tierEdit, setTierEdit] = useState(null);
  const [ruleEdit, setRuleEdit] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      loyaltyService.getProgram(),
      loyaltyService.listTiers(),
      loyaltyService.listRules(),
    ]).then(([prog, tr, rl]) => { setP(prog); setTiers(tr); setRules(rl); })
      .catch((err) => toast({ title: 'Error', description: err.message, variant: 'destructive' }));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      setP(await loyaltyService.saveProgram(p));
      toast({ title: t('loyalty.set.saved') });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const saveTier = async (e) => {
    e?.preventDefault();
    try {
      if (tierEdit.id) await loyaltyService.saveTier(tierEdit.id, tierEdit);
      else await loyaltyService.createTier(tierEdit);
      setTierEdit(null);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const saveRule = async (e) => {
    e?.preventDefault();
    try {
      if (ruleEdit.id) await loyaltyService.saveRule(ruleEdit.id, ruleEdit);
      else await loyaltyService.createRule(ruleEdit);
      setRuleEdit(null);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const dropRule = async (r) => {
    try { await loyaltyService.deleteRule(r.id); load(); }
    catch (err) { toast({ title: 'Error', description: err.message, variant: 'destructive' }); }
  };

  if (!p) return <div className="p-6 text-gray-500">{t('loyalty.common.loading')}</div>;

  return (
    <div className="p-6 w-full">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
          <Settings className="h-7 w-7 text-amber-600" /> {t('loyalty.settings')}
        </h1>
        <Button onClick={save} disabled={busy}
          className="bg-amber-600 hover:bg-amber-700 text-white">
          {t('loyalty.set.save')}
        </Button>
      </div>

      {/* Two column stacks on wide screens, one flow on small — the cards keep
          their own heights instead of a grid row stretching to the tallest. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
      <div className="space-y-4">
      {/* Identity + master switch */}
      <Card className="p-5">
        <Row label={t('loyalty.set.enable')}
          hint="Off, nothing earns or redeems; balances are kept.">
          <Toggle checked={p.is_enabled} onChange={(v) => set('is_enabled', v)} />
        </Row>
        <Row label={t('loyalty.common.name')}
          hint="What guests see on receipts and the join page.">
          <Input value={p.name} onChange={(e) => set('name', e.target.value)} />
        </Row>
        <Row label="Tagline">
          <Input value={p.tagline} onChange={(e) => set('tagline', e.target.value)} />
        </Row>
      </Card>

      {/* Earning */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-2">{t('loyalty.set.earning')}</h3>
        <Row label="Points per Rs 1"
          hint="1 = a point per rupee · 0.01 = a point per Rs 100.">
          <Input type="number" step="0.0001" min="0" value={p.points_per_currency}
            onChange={(e) => set('points_per_currency', e.target.value)} />
        </Row>
        <Row label="Earn basis"
          hint="Net of rewards: a bill a voucher paid for doesn't earn on that part.">
          <select className="w-full rounded-md border px-3 py-2 text-sm bg-background"
            value={p.earn_basis} onChange={(e) => set('earn_basis', e.target.value)}>
            <option value="BILL_TOTAL">The whole bill</option>
            <option value="NET_OF_REWARDS">The bill, less what rewards paid</option>
          </select>
        </Row>
        <Row label="Rounding">
          <select className="w-full rounded-md border px-3 py-2 text-sm bg-background"
            value={p.rounding} onChange={(e) => set('rounding', e.target.value)}>
            <option value="FLOOR">Whole points, rounded down</option>
            <option value="HALF_UP">Nearest point</option>
          </select>
        </Row>
        <Row label="Minimum bill" hint="Bills below this earn nothing.">
          <Input type="number" step="0.01" min="0" value={p.minimum_bill}
            onChange={(e) => set('minimum_bill', e.target.value)} />
        </Row>
        <Row label="Channels that earn" hint="None ticked = every channel earns.">
          <div className="flex flex-wrap gap-2">
            {['DINEIN', 'COUNTER', 'KIOSK', 'APP', 'DELIVERY'].map((ch) => {
              const on = (p.earning_channels || []).includes(ch);
              return (
                <button key={ch} type="button"
                  onClick={() => set('earning_channels',
                    on ? p.earning_channels.filter((c) => c !== ch)
                      : [...(p.earning_channels || []), ch])}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                    on ? 'border-amber-600 bg-amber-50 text-amber-800'
                      : 'border-border text-gray-500 hover:bg-accent'}`}>
                  {ch}
                </button>
              );
            })}
          </div>
        </Row>
      </Card>

      {/* Redeeming */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-2">{t('loyalty.set.redeeming')}</h3>
        <Row label="Redemption model"
          hint="Catalogue: points buy named rewards (the hospitality default). Currency: points are money at the till. Milestone: every N lifetime points mints a voucher.">
          <select className="w-full rounded-md border px-3 py-2 text-sm bg-background"
            value={p.redemption_mode} onChange={(e) => set('redemption_mode', e.target.value)}>
            <option value="CATALOGUE">Points buy rewards from the catalogue</option>
            <option value="CURRENCY">Points are worth money at the till</option>
            <option value="MILESTONE">Every milestone mints a voucher</option>
          </select>
        </Row>
        {p.redemption_mode === 'CURRENCY' && (
          <>
            <Row label="Value of 1 point (Rs)">
              <Input type="number" step="0.0001" min="0" value={p.redeem_rate}
                onChange={(e) => set('redeem_rate', e.target.value)} />
            </Row>
            <Row label="Smallest redemption (points)">
              <Input type="number" min="0" value={p.minimum_redemption_points}
                onChange={(e) => set('minimum_redemption_points', e.target.value)} />
            </Row>
            <Row label="Max % of one bill"
              hint="Cap below 100 to keep some cash in every settlement.">
              <Input type="number" step="1" min="1" max="100" value={p.max_redemption_percent}
                onChange={(e) => set('max_redemption_percent', e.target.value)} />
            </Row>
          </>
        )}
        {p.redemption_mode === 'MILESTONE' && (
          <>
            <Row label="Lifetime points per reward">
              <Input type="number" min="0" value={p.milestone_points}
                onChange={(e) => set('milestone_points', e.target.value)} />
            </Row>
            <Row label="The reward">
              <div className="flex gap-2">
                <select className="rounded-md border px-3 py-2 text-sm bg-background"
                  value={p.milestone_reward_type}
                  onChange={(e) => set('milestone_reward_type', e.target.value)}>
                  <option value="PERCENT">% off</option>
                  <option value="AMOUNT">Rs off</option>
                </select>
                <Input type="number" step="0.01" min="0" value={p.milestone_reward_value}
                  onChange={(e) => set('milestone_reward_value', e.target.value)} />
              </div>
            </Row>
            <Row label="Voucher lives (days)" hint="0 = never expires.">
              <Input type="number" min="0" value={p.milestone_validity_days}
                onChange={(e) => set('milestone_validity_days', e.target.value)} />
            </Row>
          </>
        )}
      </Card>

      {/* Expiry */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-2">{t('loyalty.set.expiry')}</h3>
        <Row label="Points expire">
          <select className="w-full rounded-md border px-3 py-2 text-sm bg-background"
            value={p.expiry_anchor} onChange={(e) => set('expiry_anchor', e.target.value)}>
            <option value="NEVER">Never</option>
            <option value="EARN_DATE">A fixed time after they were earned</option>
            <option value="LAST_ACTIVITY">After a quiet period</option>
          </select>
        </Row>
        {p.expiry_anchor !== 'NEVER' && (
          <>
            <Row label="After (days)">
              <Input type="number" min="1" value={p.expiry_days}
                onChange={(e) => set('expiry_days', e.target.value)} />
            </Row>
            <Row label="Warn ahead (days)"
              hint="The member screen and the till warn this far ahead of a lapse.">
              <Input type="number" min="0" value={p.expiry_warning_days}
                onChange={(e) => set('expiry_warning_days', e.target.value)} />
            </Row>
          </>
        )}
      </Card>
      </div>

      <div className="space-y-4">
      {/* Bonuses + enrolment */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-2">{t('loyalty.set.bonuses')}</h3>
        <Row label="Welcome bonus (points)">
          <Input type="number" min="0" value={p.signup_bonus_points}
            onChange={(e) => set('signup_bonus_points', e.target.value)} />
        </Row>
        <Row label="Birthday gift (points)" hint="Granted once a year, in the birthday month.">
          <Input type="number" min="0" value={p.birthday_bonus_points}
            onChange={(e) => set('birthday_bonus_points', e.target.value)} />
        </Row>
        <Row label="Birthday earn multiplier" hint="1 = no bonus; 2 = double points all month.">
          <Input type="number" step="0.25" min="1" value={p.birthday_multiplier}
            onChange={(e) => set('birthday_multiplier', e.target.value)} />
        </Row>
        <Row label="Referral bonus (points)"
          hint="To the member who introduced a guest, when that guest enrols.">
          <Input type="number" min="0" value={p.referral_bonus_points}
            onChange={(e) => set('referral_bonus_points', e.target.value)} />
        </Row>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-2">{t('loyalty.set.enrolment')}</h3>
        <Row label="Members only earn"
          hint="Off: any identified guest accrues, and finds a balance when they join.">
          <Toggle checked={p.enrolment_required} onChange={(v) => set('enrolment_required', v)} />
        </Row>
        <Row label="Guests may join themselves"
          hint="From the QR menu / kiosk; sign-ups arrive as leads for staff to confirm.">
          <Toggle checked={p.allow_self_enrolment} onChange={(v) => set('allow_self_enrolment', v)} />
        </Row>
        <Row label="Auto-enrol on first order"
          hint="A consent decision — off by default.">
          <Toggle checked={p.auto_enrol_on_first_order}
            onChange={(v) => set('auto_enrol_on_first_order', v)} />
        </Row>
      </Card>

      {/* Tiers */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-800">{t('loyalty.set.tiers')}</h3>
          <Button size="sm" variant="outline"
            onClick={() => setTierEdit({ name: '', qualifying_points: 0, earn_multiplier: '1.00', colour: '#C9A227', is_active: true })}>
            <Plus size={14} className="mr-1" /> {t('loyalty.set.addTier')}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Standing is held on points earned in the last 12 months — promotions land at once,
          demotions wait a full window.
        </p>
        <div className="space-y-1.5">
          {tiers.map((tier) => (
            <div key={tier.id}
              className={`flex items-center justify-between gap-2 rounded-lg border p-2.5 ${tier.is_active ? '' : 'opacity-50'}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="h-4 w-4 rounded-full shrink-0" style={{ backgroundColor: tier.colour || '#64748B' }} />
                <span className="font-medium text-gray-900">{tier.name}</span>
                <span className="text-xs text-gray-500 tabular-nums">
                  {Number(tier.qualifying_points).toLocaleString()}+ pts · ×{tier.earn_multiplier}
                </span>
                {typeof tier.member_count === 'number' && (
                  <span className="text-xs text-gray-400">· {tier.member_count} {t('loyalty.members').toLowerCase()}</span>
                )}
              </div>
              <button type="button" onClick={() => setTierEdit({ ...tier })}
                className="p-1 rounded hover:bg-accent text-gray-600"><Pencil size={14} /></button>
            </div>
          ))}
        </div>
      </Card>

      {/* Earn rules */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-800">{t('loyalty.set.rules')}</h3>
          <Button size="sm" variant="outline"
            onClick={() => setRuleEdit({ name: '', multiplier: '2.00', bonus_points: 0, weekdays: [], is_active: true })}>
            <Plus size={14} className="mr-1" /> {t('loyalty.set.addRule')}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Promotions: “double points on Tuesdays”, “triple on the buffet in December”.
          Conditions left empty mean <em>any</em>.
        </p>
        {rules.length === 0 ? (
          <p className="text-sm text-gray-500">{t('loyalty.common.none')}</p>
        ) : (
          <div className="space-y-1.5">
            {rules.map((r) => (
              <div key={r.id}
                className={`flex items-center justify-between gap-2 rounded-lg border p-2.5 ${r.is_active ? '' : 'opacity-50'}`}>
                <div className="min-w-0">
                  <span className="font-medium text-gray-900">{r.name}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    ×{r.multiplier}{Number(r.bonus_points) > 0 ? ` +${r.bonus_points}` : ''}
                    {r.location_name ? ` · ${r.location_name}` : ''}
                    {r.channel ? ` · ${r.channel}` : ''}
                    {(r.weekdays || []).length ? ` · days ${r.weekdays.join(',')}` : ''}
                  </span>
                </div>
                <div className="shrink-0">
                  <button type="button" onClick={() => setRuleEdit({ ...r })}
                    className="p-1 rounded hover:bg-accent text-gray-600"><Pencil size={14} /></button>
                  <button type="button" onClick={() => dropRule(r)}
                    className="p-1 rounded hover:bg-accent text-red-600 ml-1"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      </div>
      </div>

      {/* Tier form */}
      {tierEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setTierEdit(null)}>
          <Card className="w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {tierEdit.id ? tierEdit.name : t('loyalty.set.addTier')}
            </h2>
            <form onSubmit={saveTier} className="space-y-3">
              <Input placeholder={`${t('loyalty.common.name')} *`} required value={tierEdit.name}
                onChange={(e) => setTierEdit({ ...tierEdit, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-gray-500">Points to qualify</label>
                  <Input type="number" min="0" value={tierEdit.qualifying_points}
                    onChange={(e) => setTierEdit({ ...tierEdit, qualifying_points: e.target.value })} />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Earn multiplier</label>
                  <Input type="number" step="0.05" min="1" value={tierEdit.earn_multiplier}
                    onChange={(e) => setTierEdit({ ...tierEdit, earn_multiplier: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-[11px] text-gray-500">Badge colour</label>
                <input type="color" value={tierEdit.colour || '#C9A227'}
                  onChange={(e) => setTierEdit({ ...tierEdit, colour: e.target.value })} />
                <label className="text-[11px] text-gray-500 ml-3">Active</label>
                <Toggle checked={!!tierEdit.is_active}
                  onChange={(v) => setTierEdit({ ...tierEdit, is_active: v })} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline"
                  onClick={() => setTierEdit(null)}>{t('loyalty.common.cancel')}</Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white">
                  {t('loyalty.common.save')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Rule form */}
      {ruleEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setRuleEdit(null)}>
          <Card className="w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {ruleEdit.id ? ruleEdit.name : t('loyalty.set.addRule')}
            </h2>
            <form onSubmit={saveRule} className="space-y-3">
              <Input placeholder={`${t('loyalty.common.name')} * (it appears on earn receipts)`}
                required value={ruleEdit.name}
                onChange={(e) => setRuleEdit({ ...ruleEdit, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-gray-500">Multiplier (0 = excluded)</label>
                  <Input type="number" step="0.25" min="0" value={ruleEdit.multiplier}
                    onChange={(e) => setRuleEdit({ ...ruleEdit, multiplier: e.target.value })} />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Flat bonus points</label>
                  <Input type="number" min="0" value={ruleEdit.bonus_points}
                    onChange={(e) => setRuleEdit({ ...ruleEdit, bonus_points: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-gray-500">Days it runs (none = every day)</label>
                <div className="flex gap-1 mt-1">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => {
                    const day = i + 1;
                    const on = (ruleEdit.weekdays || []).includes(day);
                    return (
                      <button key={d} type="button"
                        onClick={() => setRuleEdit({
                          ...ruleEdit,
                          weekdays: on ? ruleEdit.weekdays.filter((x) => x !== day)
                            : [...(ruleEdit.weekdays || []), day],
                        })}
                        className={`rounded px-2 py-1 text-xs font-medium border ${
                          on ? 'border-amber-600 bg-amber-50 text-amber-800'
                            : 'border-border text-gray-500 hover:bg-accent'}`}>
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-gray-500">Channel (empty = any)</label>
                  <select className="w-full rounded-md border px-3 py-2 text-sm bg-background"
                    value={ruleEdit.channel || ''}
                    onChange={(e) => setRuleEdit({ ...ruleEdit, channel: e.target.value })}>
                    <option value="">Any</option>
                    {['DINEIN', 'COUNTER', 'KIOSK', 'APP', 'DELIVERY'].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Minimum bill</label>
                  <Input type="number" step="0.01" min="0" value={ruleEdit.minimum_bill || ''}
                    onChange={(e) => setRuleEdit({ ...ruleEdit, minimum_bill: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-gray-500">From (date)</label>
                  <Input type="date" value={ruleEdit.starts_on || ''}
                    onChange={(e) => setRuleEdit({ ...ruleEdit, starts_on: e.target.value })} />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Until (date)</label>
                  <Input type="date" value={ruleEdit.ends_on || ''}
                    onChange={(e) => setRuleEdit({ ...ruleEdit, ends_on: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline"
                  onClick={() => setRuleEdit(null)}>{t('loyalty.common.cancel')}</Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white">
                  {t('loyalty.common.save')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
